import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { X, Send, Clock, User, Hash } from 'lucide-react';
import type { DispatchLogRow } from '../api-client';

interface DispatchDetailSidebarProps {
  row: DispatchLogRow;
  onClose: () => void;
}

function Field({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] py-2 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 rounded-lg px-2 -mx-2 transition-colors">
      <span className="text-xs text-nightshade-500 dark:text-nightshade-400 flex items-center gap-1.5">
        {icon}{label}
      </span>
      <span className="text-xs text-nightshade-800 dark:text-nightshade-200 break-words">{value ?? '—'}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 whitespace-nowrap">{title}</h3>
        <div className="h-px flex-1 bg-nightshade-200 dark:bg-nightshade-700" />
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function ResultChip({ result }: { result: string }) {
  const cls =
    result === 'sent' ? 'bg-blueberry-50 dark:bg-blueberry-900/20 text-blueberry-600 dark:text-blueberry-400 border-blueberry-200 dark:border-blueberry-800'
    : result === 'skipped' ? 'bg-pomegranate-50 dark:bg-pomegranate-900/30 text-pomegranate-600 dark:text-pomegranate-400 border-pomegranate-200 dark:border-pomegranate-800'
    : result === 'failed' ? 'bg-pomegranate-100 dark:bg-pomegranate-900/30 text-pomegranate-700 dark:text-pomegranate-400 border-pomegranate-200 dark:border-pomegranate-800'
    : 'bg-nightshade-100 dark:bg-nightshade-800 text-nightshade-500 dark:text-nightshade-400 border-nightshade-200 dark:border-nightshade-700';
  const label = result === 'sent' ? 'Sent to Clay' : result.charAt(0).toUpperCase() + result.slice(1);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>
      {label}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function DispatchDetailSidebar({ row, onClose }: DispatchDetailSidebarProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const finalSubject = row.result_data?.final_subject as string | undefined;
  const finalBody = row.result_data?.final_body as string | undefined;
  const hasFinalCopy = finalSubject || finalBody;

  return (
    <div className={`flex-shrink-0 h-full overflow-hidden transition-[width] duration-200 ${visible ? 'w-[460px]' : 'w-0'}`}>
      <div className="w-[460px] h-full flex flex-col bg-white dark:bg-nightshade-950 border-l border-nightshade-200 dark:border-nightshade-800 shadow-xl">

        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-nightshade-100 dark:border-nightshade-800 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-50 truncate">
              {row.contact_name ?? 'Unknown'}
            </p>
            <p className="text-xs text-nightshade-500 dark:text-nightshade-400 truncate">
              {row.contact_email ?? '—'}
            </p>
          </div>
          <ResultChip result={row.result} />
          <button
            onClick={handleClose}
            className="ml-2 p-1 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-full"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">

          {/* Dispatch info */}
          <Section title="Dispatch Info">
            <Field label="Bucket" value={row.bucket} icon={<Hash className="w-3 h-3" />} />
            <Field label="Status Column" value={row.status_column} />
            <Field label="Channel" value={row.channel} icon={<Send className="w-3 h-3" />} />
            <Field label="Flow ID" value={row.flow_id} />
            <Field label="Sender" value={row.sender_email} icon={<User className="w-3 h-3" />} />
            <Field label="Dispatched at" value={formatDate(row.dispatched_at)} icon={<Clock className="w-3 h-3" />} />
            {row.is_test && (
              <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blueberry-50 dark:bg-blueberry-900/20 text-blueberry-500 border border-blueberry-200 dark:border-blueberry-800">
                Test dispatch
              </div>
            )}
          </Section>

          {/* Sent copy (original subject/body from dispatch) */}
          {(row.subject || row.body) && (
            <Section title="Sent Copy">
              {row.subject && (
                <div className="mb-2">
                  <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 mb-1">Subject</p>
                  <p className="text-xs text-nightshade-800 dark:text-nightshade-200">{row.subject}</p>
                </div>
              )}
              {row.body && (
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 mb-1">Body</p>
                  <pre className="text-xs text-nightshade-700 dark:text-nightshade-300 whitespace-pre-wrap font-sans leading-relaxed bg-nightshade-50 dark:bg-nightshade-900 border border-nightshade-100 dark:border-nightshade-800 rounded-lg px-3 py-3">
                    {row.body}
                  </pre>
                </div>
              )}
            </Section>
          )}

          {/* Final copy from Clay */}
          {hasFinalCopy && (
            <Section title="Final Copy (from Clay)">
              {finalSubject && (
                <div className="mb-2">
                  <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 mb-1">Subject</p>
                  <p className="text-xs text-nightshade-800 dark:text-nightshade-200">{finalSubject}</p>
                </div>
              )}
              {finalBody && (
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 mb-1">Body</p>
                  <div
                    className="text-xs text-nightshade-700 dark:text-nightshade-300 whitespace-pre-wrap font-sans leading-relaxed bg-nightshade-50 dark:bg-nightshade-900 border border-nightshade-100 dark:border-nightshade-800 rounded-lg px-3 py-3 prose prose-xs dark:prose-invert max-w-none [&_p]:my-1"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(finalBody) }}
                  />
                </div>
              )}
            </Section>
          )}

          {/* Result statuses */}
          <Section title="Results">
            <Field label="Launch Result" value={<ResultChip result={row.result} />} />
            {row.result_status && (
              <Field label="Clay Result" value={row.result_status} />
            )}
            {row.result_at && (
              <Field label="Result at" value={formatDate(row.result_at)} />
            )}
            {row.error && (
              <div className="mt-2 bg-pomegranate-50 dark:bg-pomegranate-900/20 border border-pomegranate-200 dark:border-pomegranate-800 rounded-lg px-3 py-2">
                <p className="text-[10px] font-medium text-pomegranate-600 dark:text-pomegranate-400 uppercase tracking-wide mb-0.5">Error</p>
                <p className="text-xs text-pomegranate-700 dark:text-pomegranate-300">{row.error}</p>
              </div>
            )}
          </Section>

          {/* Raw result_data (if any extra fields beyond final_subject/body) */}
          {row.result_data && Object.keys(row.result_data).filter(k => !['final_subject', 'final_body'].includes(k)).length > 0 && (
            <Section title="Raw Result Data">
              <pre className="text-[10px] text-nightshade-500 dark:text-nightshade-400 whitespace-pre-wrap font-mono bg-nightshade-50 dark:bg-nightshade-900 border border-nightshade-100 dark:border-nightshade-800 rounded-lg px-3 py-3 max-h-40 overflow-y-auto">
                {JSON.stringify(
                  Object.fromEntries(Object.entries(row.result_data).filter(([k]) => !['final_subject', 'final_body'].includes(k))),
                  null, 2,
                )}
              </pre>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
